import { get } from 'lodash';

import { EnvironmentCountries } from '@e-origin/shared';

import { useSettings } from '@hooks/useSettings';

const POSTAL_CODE_HEERLEN = '6401 DN Heerlen';

export const translations = {
  HEADER: {
    DATE: {
      EN: 'DATE: {{date}}, {{time}}',
      NL: 'DATUM: {{date}}, {{time}}',
      BE: 'DATE: {{date}}, {{time}}',
    },
  },
  SUB_HEADER: {
    DECLARANT: {
      EN: 'Declarant',
      NL: 'Aangever',
      BE: 'Déclarant',
    },
    EORI_NUMBER: {
      EN: 'EORI Number',
      NL: 'Eorinummer',
      BE: 'Numéro EORI',
    },
    NAME: {
      EN: 'Name',
      NL: 'Naam',
      BE: 'Nom',
    },
    ADDRESS: {
      EN: 'Address',
      NL: 'Adres',
      BE: 'Adresse',
    },
    REPRESENTATIVE: {
      EN: 'Representative',
      NL: 'Vertegenwoordiger',
      BE: 'Représentant',
    },
    COMMERCIAL_REF: {
      EN: 'Commercial ref: {{commercialReference}}',
      NL: 'Commerciële ref: {{commercialReference}}',
      BE: 'Commercial ref: {{commercialReference}}',
    },
    DECLARATION_NUMBER: {
      EN: 'Declaration Number: {{movementReference}}',
      NL: 'Aangiftenummer: {{movementReference}}',
      BE: 'Numéro de déclaration: {{movementReference}}',
    },
    NUMBER_ARTICLES: {
      EN: 'Number of articles: {{value}}',
      NL: 'Aantal artikelen: {{value}}',
      BE: "Nombre d'articles: {{value}}",
    },
    ACCEPTANCE_DATE: {
      EN: 'Acceptance date: {{date}}',
      NL: 'Aanvaardingsdatum: {{date}}',
      BE: "Date d'acceptation: {{date}}",
    },
    RESIDENCE: {
      EN: 'Residence',
      NL: 'Woonplaats',
      BE: 'Résidence',
    },
    VAT_LIABLE_PERSON: {
      EN: 'VAT liable person',
      NL: 'OB plichtige',
    },
    IMPORTER: {
      EN: 'Importer',
      NL: 'Importeur',
    },
  },
  OBJECTION_CLAUSE: {
    DISAGREE: {
      EN: 'Do you disagree with this decision? Then you can object. Send a letter to Customs/Objection and Appeal Team',
      NL: 'Bent u het niet eens met deze beslissing? Dan kunt u bezwaar maken. Stuur een brief naar Douane/Team Bezwaar en Beroep',
      BE: "Êtes-vous en désaccord avec cette décision? Vous pouvez alors vous y opposer. Envoyez une lettre à l'équipe des douanes/objections et appels",
    },
    PO_BOX: {
      EN: 'PO Box 3070',
      NL: 'Postbus 3070',
      BE: 'Boîte Postale 3070',
    },
    POSTAL_CODE: {
      EN: POSTAL_CODE_HEERLEN,
      NL: POSTAL_CODE_HEERLEN,
      BE: POSTAL_CODE_HEERLEN,
    },
    REASON: {
      EN: 'Always state the reason for your objection and the number of this declaration. We must receive your objection before {{date}}',
      NL: 'Vermeld altijd de reden van uw bezwaar en het nummer van deze aangifte. Uw bezwaar moet bij ons binnen zijn vóór {{date}}',
      BE: 'Indiquez toujours le motif de votre objection et le numéro de cette déclaration. Nous devons recevoir votre objection avant le {{date}}',
    },
  },
  RESOURCES: {
    DECLARATION_NUMBER: {
      EN: 'Declaration Number: {{movementReference}}',
      NL: 'Aangiftenummer: {{movementReference}}',
      BE: 'Numéro de déclaration: {{movementReference}}',
    },
    SEQUENCE: {
      EN: 'Sequence: {{sequence}}',
      NL: 'Volgorde: {{sequence}}',
      BE: 'Séquence: {{sequence}}',
    },
    REFERENCE_NUMBER_UCR: {
      EN: 'Reference Number UCR: {{ucr}}',
      NL: 'Referentienummer UCR: {{ucr}}',
    },
    SHIPPING_MARKS: {
      EN: 'Shipping marks: {{shippingMarks}}',
      NL: 'Shipping marks: {{shippingMarks}}',
    },
    COMMODITY_CODE: {
      HS_CODE: {
        EN: 'HS Code: {{hsCode}}',
      },
      INVOICE_DESCRIPTION: {
        EN: 'Invoice Description: {{invoiceDescription}}',
        NL: 'Factuuromschrijving: {{invoiceDescription}}',
        BE: 'Description sur la facture: {{invoiceDescription}}',
      },
    },
    RESOURCE: {
      EN: 'Resource {{taxType}}',
      NL: 'Middel {{taxType}}',
      BE: 'Ressource {{taxType}}',
    },
    IMPORT_DUTIES: {
      EN: 'Import duties',
      NL: 'Invoerrechten',
    },
    VAT: {
      EN: 'VAT',
      NL: 'BTW',
    },
    TAXABLE_AMOUNT: {
      EN: 'Taxable Amount: {{amount}}',
      NL: 'Belastbaar bedrag: {{amount}}',
      BE: 'Montant imposable: {{amount}}',
    },
    RATE: {
      EN: 'Rate: {{rate}}',
      NL: 'Tarief: {{rate}}',
      BE: 'Taux: {{rate}}',
    },
    RATE_PER_EUR: {
      EN: 'Rate: {{rate}} per {{unit}} €',
      NL: 'Tarief: {{rate}} per {{unit}} €',
      BE: 'Taux: {{rate}} par {{unit}} €',
    },
    DUE: {
      EN: 'Due',
      NL: 'Verschuldigd',
      BE: 'Dû',
    },
  },
  TOTAL_PER_RESOURCE: {
    TITLE: {
      EN: 'Total per resource',
      NL: 'Totaal per middel',
      BE: 'Total par ressource',
    },
  },
  TOTAL: {
    INVITATIONS: {
      EN: 'Total invitation(s)',
      NL: 'Totaal uitnodiging(en)',
      BE: 'Total invitation(s)',
    },
    DUE: {
      EN: 'Final amount due',
      NL: 'Definitief verschuldigd',
    },
  },
};

export const getTranslation = (path: string, language = 'EN', variables: Record<string, any> = {}): string => {
  const value = get(translations, path);

  let translatedText = value?.[language] || value?.EN || path;

  // Replace variables in the format {{variableName}} or {{ variableName }}
  if (variables && Object.keys(variables).length > 0) {
    translatedText = translatedText.replace(/\{\{\s*([^}]+)\s*\}\}/g, (match, variableName) => {
      const trimmedName = variableName.trim();
      return variables[trimmedName] !== undefined ? variables[trimmedName] : match;
    });
  }

  return translatedText;
};

export const useTranslation = () => {
  const { data: settings } = useSettings();
  const language = settings.country === EnvironmentCountries.BE ? 'EN' : settings.country;
  const t = (path: string, variables: Record<string, any> = {}): string => getTranslation(path, language, variables);

  return { t };
};
